import type { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { currencyDisplay } from '~/components/utils/text';
import { longTermDiscountApplied, lastMinuteDiscountApplied, getComputedPrice, percentageOf } from './utils';
import type { CoreOfficeDataType } from '~/types/offices';

type TProps = {
  office: CoreOfficeDataType;
  moveinDate: DateTime | null;
  moveoutDate: DateTime | null;
  requiredCapacity: number | null;
  daysPerWeek: number | null;
  extraClasses?: string;
};

const PriceBreakdown = ({
  office,
  moveinDate,
  moveoutDate,
  requiredCapacity,
  daysPerWeek,
  extraClasses = '',
}: TProps) => {
  const { t } = useTranslation();

  const longTermDiscountApplies = longTermDiscountApplied(office.discountLongTermMonths, moveinDate, moveoutDate);
  const lastMinuteDiscountApplies = lastMinuteDiscountApplied(office.discountLastMinuteDays, moveinDate);
  const discountApplies = longTermDiscountApplies || lastMinuteDiscountApplies;

  const computedTotalPrice = Math.ceil(getComputedPrice(requiredCapacity, daysPerWeek, office));

  const getComputedPriceAfterDiscounts = () => {
    let price = computedTotalPrice;
    if (longTermDiscountApplies) {
      price = price - percentageOf(office.discountLongTermPercent, computedTotalPrice);
    }
    if (lastMinuteDiscountApplies) {
      price = price - percentageOf(office.discountLastMinutePercent, computedTotalPrice);
    }
    return price;
  };

  if (!requiredCapacity || !daysPerWeek) return <></>;

  const seatsLabel = requiredCapacity > 1 ? t('global.pluralization.seat.other') : t('global.pluralization.seat.one');

  return (
    <div className={extraClasses}>
      {discountApplies && (
        <div className="d-flex justify-content-between mb-2">
          <div className="text-2 -regular color-grey-500">
            {`${currencyDisplay(computedTotalPrice / requiredCapacity, 0)} x ${requiredCapacity} ${seatsLabel}`}
          </div>
          <div className="text-2 -regular color-grey-500">
            {currencyDisplay(computedTotalPrice, 0)}/{t('global.pluralization.month.one')}
          </div>
        </div>
      )}
      {longTermDiscountApplies && (
        <div className="d-flex justify-content-between mb-2">
          <div className="text-2 -regular color-grey-500">
            {t('office.show.price.discounts.longTerm.labelWithPercentage', {
              percentage: office.discountLongTermPercent,
            })}
          </div>
          <div className="text-2 -regular color-grey-500">
            -{currencyDisplay(percentageOf(office.discountLongTermPercent, computedTotalPrice), 0)}
          </div>
        </div>
      )}

      {lastMinuteDiscountApplies && (
        <div className="d-flex justify-content-between mb-2">
          <div className="text-2 -regular color-grey-500">
            {t('office.show.price.discounts.lastMinute.labelWithPercentage', {
              percentage: office.discountLastMinutePercent,
            })}
          </div>
          <div className="text-2 -regular color-grey-500">
            -{currencyDisplay(percentageOf(office.discountLastMinutePercent, computedTotalPrice), 0)}
          </div>
        </div>
      )}

      <div className="d-flex align-items-center justify-content-between mb-2">
        <div>
          <div className="text-1 -medium">{t('office.show.requestVisitCard.totalPricePerMonth.label')}</div>
          {!discountApplies && (
            <div className="text-2 -regular color-grey-500">
              {`${currencyDisplay(computedTotalPrice / requiredCapacity, 0)} x ${requiredCapacity} ${seatsLabel}`}
            </div>
          )}
        </div>
        <div>
          <span className="fo-display-4">{currencyDisplay(getComputedPriceAfterDiscounts(), 0)} </span>
          <span className="caption -regular">{t('office.show.requestVisitCard.totalPricePerMonth.vatExcluded')}</span>
        </div>
      </div>
    </div>
  );
};

export default PriceBreakdown;
